import React from 'react';
import Layout from '../components/Layout';
import { FaUserAstronaut, FaBolt, FaBriefcase, FaGraduationCap, FaMedal, FaServer, FaCode, FaRobot, FaFileDownload } from 'react-icons/fa';

const About = () => {
  const stats = [
    { name: 'AI & Machine Learning', value: 95, icon: <FaRobot />, color: 'bg-purple-500' },
    { name: 'Full Stack Development', value: 90, icon: <FaCode />, color: 'bg-cyan-500' },
    { name: 'Cloud & DevOps', value: 85, icon: <FaServer />, color: 'bg-blue-500' },
    { name: 'Problem Solving', value: 98, icon: <FaBolt />, color: 'bg-yellow-500' },
  ];

  const experience = [
    {
      role: "Lead AI & Automations Developer",
      company: "Tax Consulting South Africa",
      period: "Sept 2025 - Present",
      desc: "Designing AI strategies, leading full project lifecycles, and integrating ML/RPA technologies to optimize business operations."
    },
    {
      role: "Full Stack Developer",
      company: "CipherGenix",
      period: "April 2025 - Present",
      desc: "Building distributed AI cybersecurity platforms with microservices, secure payments, and real-time threat detection."
    },
    {
      role: "AI Trainer",
      company: "Outlier.AI",
      period: "Feb 2025 - Present",
      desc: "Improving LLM accuracy, optimizing AI-generated code across multiple languages, and reducing error rates."
    },
    {
      role: "Software Developer Trainee",
      company: "WeThinkCode_",
      period: "Sept 2022 - Dec 2023",
      desc: "Built full-stack platforms and strengthened skills in Java, Python, and Agile methodologies."
    }
  ];

  const education = [
    {
      degree: "BCom Information Systems",
      school: "University of Johannesburg",
      year: "Graduated"
    },
    {
      degree: "IT Certificate (Systems Development)",
      school: "WeThinkCode_",
      year: "2023"
    },
    {
      degree: "BHS Sport and Exercise Science",
      school: "University of Johannesburg",
      year: "Graduated"
    }
  ];

  const certificates = [
    "Hackerrank (Software Engineer)",
    "IBM (Artificial Intelligence)",
    "Udacity (Generative AI)",
    "Kubernetes for Developers"
  ];

  return (
    <Layout>
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-8 rounded-2xl shadow-2xl border border-slate-700 max-w-5xl mx-auto">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row gap-12 items-center mb-16">
          {/* Avatar */}
          <div className="relative group shrink-0">
            <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 to-blue-600 rounded-full blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
            <div className="relative w-48 h-48 bg-slate-800 rounded-full flex items-center justify-center border-4 border-slate-900">
              <FaUserAstronaut className="text-8xl text-cyan-400" />
            </div>
            <div className="absolute bottom-0 right-0 bg-green-500 text-xs font-bold px-2 py-1 rounded border border-slate-900">
              ONLINE
            </div>
          </div>

          {/* Bio */}
          <div className="flex-1">
            <h2 className="text-4xl font-bold mb-2 flex items-center gap-3">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                Player Profile
              </span>
            </h2>
            <p className="text-gray-400 font-mono mb-6 text-sm border-l-2 border-cyan-500 pl-4">
              CLASS: AI_FULL_STACK_ENGINEER<br/>
              SPECIALIZATION: INTELLIGENT_SYSTEMS
            </p>

            <p className="text-lg text-gray-300 mb-6 leading-relaxed">
              AI Full-Stack Systems Engineer skilled in building scalable applications with Spring Boot, Flask, FastAPI, React.js, and Angular. 
              Specializes in AI and machine learning using Python, TensorFlow, PyTorch, and Hugging Face. 
              Adept in cloud-native architectures (Azure, GCP), containerized deployments, and secure CI/CD pipelines.
            </p>

            <div className="flex gap-4 mb-8">
              <a 
                href="/Assets/CV.pdf" 
                download="Sphesihle_Mhlongo_CV.pdf"
                className="bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-2 px-6 rounded-full transition-all duration-300 shadow-[0_0_15px_rgba(6,182,212,0.4)] hover:shadow-[0_0_25px_rgba(6,182,212,0.6)] active:scale-95 flex items-center gap-2"
              >
                <FaFileDownload /> Download CV
              </a>
            </div>

            {/* Stats Bars */}
            <div className="space-y-3">
              {stats.map((stat, index) => (
                <div key={index}>
                  <div className="flex justify-between mb-1">
                    <span className="text-xs font-bold text-cyan-300 flex items-center gap-2 uppercase tracking-wider">
                      {stat.icon} {stat.name}
                    </span>
                    <span className="text-xs font-bold text-gray-400">{stat.value}/100</span>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div 
                      className={`${stat.color} h-2 rounded-full transition-all duration-1000 ease-out shadow-[0_0_10px_currentColor]`} 
                      style={{ width: `${stat.value}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* Experience Section */}
            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-cyan-500/50 transition-colors">
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-3 text-cyan-400">
                    <FaBriefcase /> Quest History
                </h3>
                <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-600 before:to-transparent">
                    {experience.map((job, index) => (
                        <div key={index} className="relative flex items-start group">
                            <div className="absolute left-0 ml-5 -translate-x-1/2 mt-1.5 w-3 h-3 rounded-full border-2 border-slate-900 bg-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.8)]"></div>
                            <div className="ml-10">
                                <h4 className="text-lg font-bold text-white group-hover:text-cyan-300 transition-colors">{job.role}</h4>
                                <div className="text-sm text-cyan-500 font-mono mb-1">{job.company} | {job.period}</div>
                                <p className="text-sm text-gray-400 leading-relaxed">{job.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Education & Certs Column */}
            <div className="space-y-8">
                
                {/* Education */}
                <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-purple-500/50 transition-colors">
                    <h3 className="text-2xl font-bold mb-6 flex items-center gap-3 text-purple-400">
                        <FaGraduationCap /> Training Grounds
                    </h3>
                    <div className="space-y-4">
                        {education.map((edu, index) => (
                            <div key={index} className="flex items-center gap-4 p-3 rounded-lg bg-slate-800 border border-slate-700">
                                <div className="w-10 h-10 rounded-full bg-purple-900/50 flex items-center justify-center text-purple-400 shrink-0">
                                    <FaGraduationCap />
                                </div>
                                <div>
                                    <h4 className="font-bold text-white text-sm">{edu.degree}</h4>
                                    <p className="text-xs text-gray-400">{edu.school}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Certificates */}
                <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 hover:border-yellow-500/50 transition-colors">
                    <h3 className="text-2xl font-bold mb-6 flex items-center gap-3 text-yellow-400">
                        <FaMedal /> Achievements
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        {certificates.map((cert, index) => (
                            <span key={index} className="px-3 py-1 rounded-full bg-yellow-900/20 text-yellow-200 border border-yellow-700/50 text-xs font-mono flex items-center gap-2">
                                <FaMedal size={10} /> {cert}
                            </span>
                        ))}
                    </div>
                </div>

            </div>
        </div>

      </div>
    </Layout>
  );
};

export default About;
